/*
 * MUI theme. Mirrors the --gp-* tokens in tokens.css so MUI
 * components (Button, TextField, Paper) inherit the GoPower brand
 * without per component overrides. Hex values here must stay in sync
 * with src/theme/tokens.css. That file is the source of truth.
 *
 * Not paired with <CssBaseline/> at the app root since legacy
 * screens still rely on their own CSS and Bootstrap, and a global
 * baseline reset would break them.
 */
import { createTheme } from '@mui/material/styles';

const gp = {
  bg: '#FFFFFF',
  surface: '#FBFBF9',
  border: '#E8E8E4',
  ink: '#0B2926',
  inkMute: '#6B7673',
  brand: '#005339',
  accent: '#478469',
  lime: '#B6F273',
  limeSoft: '#DBFFAA',
  warn: '#B46A16',
  warnSoft: '#FCE8C8',
  danger: '#B42222',
  info: '#2A5FB4',
  infoSoft: '#DCE8FB',
  page: '#F3F1EC',
  card: '#FFFFFF',
  hairline: '#EDEBE4',
  shadowSm: '0 1px 2px rgba(11, 41, 38, 0.04), 0 2px 6px rgba(11, 41, 38, 0.03)',
  shadowMd: '0 1px 2px rgba(11, 41, 38, 0.05), 0 8px 24px rgba(11, 41, 38, 0.05)',
  fontSans: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
  fontMono: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
  rSm: 8,
  rMd: 14,
  rLg: 20,
  rXl: 28,
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: gp.brand, contrastText: '#FFFFFF' },
    secondary: { main: gp.accent, contrastText: '#FFFFFF' },
    error: { main: gp.danger, contrastText: '#FFFFFF' },
    warning: { main: gp.warn, contrastText: '#FFFFFF' },
    info: { main: gp.info, contrastText: '#FFFFFF' },
    success: { main: gp.brand, contrastText: '#FFFFFF' },
    text: { primary: gp.ink, secondary: gp.inkMute },
    background: { default: gp.page, paper: gp.card },
    divider: gp.border,
  },

  shape: { borderRadius: gp.rMd },

  typography: {
    fontFamily: gp.fontSans,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 600,
    h1: { fontWeight: 600, fontSize: '40px', lineHeight: 1.05 },
    h2: { fontWeight: 500, fontSize: '28px', lineHeight: 1.15 },
    h3: { fontWeight: 500, fontSize: '22px', lineHeight: 1.2 },
    h4: { fontWeight: 500, fontSize: '17px', lineHeight: 1.3 },
    body1: { fontWeight: 400, fontSize: '15px', lineHeight: 1.45 },
    body2: { fontWeight: 400, fontSize: '13px', lineHeight: 1.4 },
    button: { fontWeight: 500, fontSize: '14px', lineHeight: 1, textTransform: 'none' },
    caption: { fontWeight: 400, fontSize: '13px', lineHeight: 1.4 },
    overline: { fontWeight: 500, fontSize: '11px', lineHeight: 1.3, letterSpacing: 0.8, textTransform: 'uppercase' },
  },

  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 999,
          textTransform: 'none',
          fontWeight: 500,
          height: 44,
          padding: '0 20px',
        },
        containedPrimary: {
          '&:hover': { backgroundColor: '#00422D' },
        },
        outlinedPrimary: {
          borderColor: gp.brand,
          '&:hover': { backgroundColor: gp.limeSoft, borderColor: gp.brand },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: gp.rLg },
        elevation1: { boxShadow: gp.shadowSm },
        elevation2: { boxShadow: gp.shadowMd },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: gp.rSm },
      },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: { root: { color: gp.brand } },
    },
  },
});

export { gp };
export default theme;
